import React from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function tl(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return `₺${v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const INPUT_CLS =
    "w-full rounded-lg bg-slate-950/80 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-slate-400 focus:outline-none";
export const SELECT_CLS = INPUT_CLS;

export function Field({ label, children, hint }) {
    return (
        <label className="block space-y-1.5">
            <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">{label}</span>
            {children}
            {hint ? <span className="text-[10px] text-slate-600 block leading-snug">{hint}</span> : null}
        </label>
    );
}

export function SectionCard({ title, desc, children, defaultOpen = true }) {
    const [open, setOpen] = React.useState(defaultOpen);
    return (
        <div className="rounded-xl border border-white/10 bg-slate-950/30 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-white/[0.02]"
            >
                <div>
                    <div className="text-sm font-medium text-white">{title}</div>
                    {desc ? <div className="text-[11px] text-slate-500 mt-0.5">{desc}</div> : null}
                </div>
                {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </button>
            {open ? <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">{children}</div> : null}
        </div>
    );
}

export function KpiCard({ label, value, hint, accent = "neutral" }) {
    const ring =
        accent === "gold"
            ? "border-[#D4AF37]/25 bg-[#121A2F]/90"
            : accent === "emerald"
              ? "border-emerald-500/20 bg-emerald-950/20"
              : accent === "sky"
                ? "border-sky-500/20 bg-sky-950/20"
                : "border-white/10 bg-slate-900/40";
    return (
        <div className={`rounded-xl border p-4 ${ring}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-display text-lg text-white mt-1 break-words">{value}</div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1.5">{hint}</div> : null}
        </div>
    );
}

export function WorkflowStep({ n, label, active, done }) {
    return (
        <div className={`flex items-center gap-2 text-xs ${active ? "text-white" : done ? "text-slate-400" : "text-slate-600"}`}>
            <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border shrink-0 ${
                    active
                        ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                        : done
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                          : "border-white/10"
                }`}
            >
                {done ? "✓" : n}
            </span>
            <span className="hidden sm:inline">{label}</span>
        </div>
    );
}

export function PanelHeader({ title, action }) {
    return (
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/10 bg-slate-900/50">
            <span className="text-sm font-medium text-white">{title}</span>
            {action}
        </div>
    );
}

export function DataTable({ columns, rows, highlightFirst }) {
    if (!rows?.length) return <p className="text-sm text-slate-500 px-4 py-6">Veri yok.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
                <thead>
                    <tr className="text-slate-500 border-b border-white/10">
                        {columns.map((c) => (
                            <th key={c.key} className={`px-3 py-2 font-medium ${c.align === "right" ? "text-right" : "text-left"}`}>
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr
                            key={row._key ?? i}
                            className={`border-t border-white/5 hover:bg-white/[0.02] ${highlightFirst && i === 0 ? "bg-white/[0.03]" : ""}`}
                        >
                            {columns.map((c) => (
                                <td key={c.key} className={`px-3 py-2 text-slate-300 ${c.align === "right" ? "text-right tabular-nums" : ""}`}>
                                    {c.render ? c.render(row) : row[c.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function AlertBox({ items }) {
    if (!items?.length) return null;
    return (
        <>
            {items.map((u, i) => (
                <div key={i} className="flex gap-2 rounded-lg border border-amber-500/20 bg-amber-950/20 px-4 py-3 text-sm text-amber-100/90">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
                    {u}
                </div>
            ))}
        </>
    );
}

export function EmptyResult() {
    return (
        <div className="rounded-xl border border-dashed border-white/10 bg-slate-950/20 p-16 text-center">
            <div className="w-14 h-14 rounded-2xl border border-white/10 bg-slate-900/50 flex items-center justify-center mx-auto mb-4 text-slate-500">
                <CheckCircle2 className="h-7 w-7 opacity-40" />
            </div>
            <p className="text-slate-400 text-sm max-w-md mx-auto">
                Sol panelden parametreleri girin ve <span className="text-[#D4AF37] font-medium">Hesapla</span> ile TRH-2010
                progresif rant cetveli oluşturun.
            </p>
        </div>
    );
}

export function SearchInput({ value, onChange, placeholder }) {
    return (
        <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="h-9 bg-slate-950 border-white/10 text-sm"
        />
    );
}

export function IconTab({ tabs, active, onChange }) {
    return (
        <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-950/60 border border-white/10">
            {tabs.map((t) => {
                const Icn = t.icon;
                const on = active === t.id;
                return (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => onChange(t.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition ${
                            on ? "bg-white/10 text-white shadow-sm" : "text-slate-500 hover:text-slate-300"
                        }`}
                    >
                        {Icn ? <Icn className="h-3.5 w-3.5" /> : null}
                        {t.label}
                    </button>
                );
            })}
        </div>
    );
}

export function GhostBtn({ children, ...props }) {
    return (
        <Button type="button" size="sm" variant="ghost" className="h-8 text-slate-400 hover:text-white" {...props}>
            {children}
        </Button>
    );
}
